/**
 * App Router — TV-RV-07 / TV-RV-11
 *
 * Changes from pre-slice1:
 *   - Index route (`/`) now uses a role-aware redirect via RoleAwareRedirect
 *     instead of hard-coding Navigate to "queue".
 *   - Clinical routes (careplan, assessment, ai-docs, ai-agent) are wrapped
 *     in <RequireRole allow={['provider']}> — non-providers deep-linking to
 *     these routes are silently redirected to their own home route.
 *   - /sign (ProviderSoapReview) also guarded to provider-only.
 *   - The dead /admin branch in SmartCallback has been replaced by VIEW_CONFIG
 *     routing — no /admin route exists in App.jsx (admin home = /queue).
 *   - /queue and /billing are accessible to both provider and front-desk
 *     (front-desk billing is read-only via cfg.canSeeClinical — TV-RV-10).
 *   - TV-RV-11: /schedule route added, front-desk + provider only.
 *
 * Route structure:
 *   /launch?iss=...&launch=...  → EHR Launch (SMART on FHIR)
 *   /callback                    → OAuth2 callback (both EHR and standalone)
 *   /guest-visit                 → Unauthenticated guest join
 *   /queue                       → Provider/front-desk waiting room queue
 *   /schedule                    → Front-desk scheduling surface (TV-RV-11)
 *   /waiting-room                → Patient waiting room
 *   /visit/:encounterId          → Live video visit
 *   /assessment/:encounterId     → SDC assessments (provider only)
 *   /careplan/:encounterId       → Care plan management (provider only)
 *   /ai-docs/:encounterId        → Ambient AI documentation (provider only)
 *   /ai-agent/:encounterId       → AI patient companion (provider only)
 *   /billing/:encounterId        → Time-based billing (provider + front-desk)
 *   /summary/:encounterId        → Post-visit summary (provider + front-desk + patient)
 *   /sign/:encounterId           → Provider SOAP review + sign (provider only)
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { SmartLaunch } from './auth/SmartLaunch';
import { SmartCallback } from './auth/SmartCallback';
import { RequireRole } from './auth/RequireRole';
import { getViewConfig } from './auth/viewConfig';
import AppShell from './layout/AppShell';
import ProviderQueue from './features/waiting-room/ProviderQueue';
import PatientWaitingRoom from './features/waiting-room/PatientWaitingRoom';
import LiveVisit from './features/visit/LiveVisit';
import GuestVisit from './features/visit/GuestVisit';
import Assessment from './features/assessment/Assessment';
import CarePlan from './features/careplan/CarePlan';
import AIDocs from './features/ai-docs/AIDocs';
import AIPatientAgent from './features/ai-agent/AIPatientAgent';
import Billing from './features/billing/Billing';
import PostVisitSummary from './features/post-visit/PostVisitSummary';
import ProviderSoapReview from './features/post-visit/ProviderSoapReview';
import ScheduleSurface from './features/schedule/ScheduleSurface';
import LoadingScreen from './components/LoadingScreen';

/**
 * ProtectedRoute
 * Guards app routes — redirects to appropriate login depending on auth mode.
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <LoadingScreen />;

  if (!isAuthenticated) {
    // Keycloak standalone mode handles its own redirect via onLoad: 'login-required'
    // So if we get here unauthenticated, something went wrong — show loading
    // (Keycloak will redirect to its login page automatically)
    return <LoadingScreen />;
  }

  return children;
}

/**
 * RoleAwareRedirect
 * TV-RV-07 — replaces the hard-coded `Navigate to="queue"` on the index route.
 * Sends each role to their VIEW_CONFIG home so patients land on /waiting-room
 * and front-desk lands on /queue without any special-casing here.
 */
function RoleAwareRedirect() {
  const { user } = useAuth();
  const cfg = getViewConfig(user?.role ?? 'patient');
  return <Navigate to={cfg.home} replace />;
}

export default function App() {
  return (
    <Routes>
      {/* ── SMART on FHIR EHR Launch ── */}
      <Route path="/launch" element={<SmartLaunch />} />

      {/* ── OAuth2 Callback (fhirclient handles token exchange) ── */}
      <Route path="/callback" element={<SmartCallback />} />

      {/* ── Guest join (TV-P3-06): interpreter / observer comes in via
           an invite link, NOT via SMART/KC. The token in the URL hash
           is short-lived and room-scoped. */}
      <Route path="/guest-visit" element={<GuestVisit />} />

      {/* ── Protected Application Routes ── */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        {/* TV-RV-07 — role-aware default redirect */}
        <Route index element={<RoleAwareRedirect />} />

        {/* ── Shared / multi-role routes ── */}
        {/* provider + front-desk can both see the queue */}
        <Route path="queue" element={<ProviderQueue />} />

        {/* TV-RV-11: scheduling surface — front-desk primary, provider also allowed */}
        <Route
          path="schedule"
          element={
            <RequireRole allow={['front-desk', 'provider']}>
              <ScheduleSurface />
            </RequireRole>
          }
        />

        {/* patient waiting room */}
        <Route path="waiting-room" element={<PatientWaitingRoom />} />

        {/* visit: all roles that canJoinVisit */}
        <Route path="visit/:encounterId" element={<LiveVisit />} />

        {/* billing: provider + front-desk (read-only for front-desk enforced in component, Slice 2) */}
        <Route
          path="billing/:encounterId"
          element={
            <RequireRole allow={['provider', 'front-desk']}>
              <Billing />
            </RequireRole>
          }
        />

        {/* summary: provider + front-desk + patient */}
        <Route
          path="summary/:encounterId"
          element={
            <RequireRole allow={['provider', 'front-desk', 'patient']}>
              <PostVisitSummary />
            </RequireRole>
          }
        />

        {/* ── Provider-only clinical routes ── */}
        {/* TV-RV-07: these routes redirect non-providers to their home */}
        <Route
          path="assessment/:encounterId"
          element={
            <RequireRole allow={['provider']}>
              <Assessment />
            </RequireRole>
          }
        />
        <Route
          path="careplan/:encounterId"
          element={
            <RequireRole allow={['provider']}>
              <CarePlan />
            </RequireRole>
          }
        />
        <Route
          path="ai-docs/:encounterId"
          element={
            <RequireRole allow={['provider']}>
              <AIDocs />
            </RequireRole>
          }
        />
        <Route
          path="ai-agent/:encounterId"
          element={
            <RequireRole allow={['provider']}>
              <AIPatientAgent />
            </RequireRole>
          }
        />
        {/* TV-P7-09 — provider SOAP review + sign before summary */}
        <Route
          path="sign/:encounterId"
          element={
            <RequireRole allow={['provider']}>
              <ProviderSoapReview />
            </RequireRole>
          }
        />
      </Route>
    </Routes>
  );
}
